import { type DriverIdleEngineEventsRequest, type IdleEngineAggregationsRequest, IdleEngineRequester, type VehicleIdleEngineEventsRequest } from '@cobliteam/api-lib';

import type { RawDriverIdleEngine, RawDriverIdleEngineEvent, RawVehicleIdleEngine, RawVehicleIdleEngineEvent } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { useQuery } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getIdleEngineRequester = () => new IdleEngineRequester({ host: env.apiHost });

const VEHICLE_AGGREGATED_KEY = 'VEHICLE-AGGREGATED-IDLE-ENGINE';
const DRIVER_AGGREGATED_KEY = 'DRIVER-AGGREGATED-IDLE-ENGINE';
const VEHICLE_EVENTS_KEY = 'VEHICLE-EVENTS-IDLE-ENGINE';
const DRIVER_EVENTS_KEY = 'DRIVER-EVENTS-IDLE-ENGINE';

const useIdleEngine = () => {
  const getVehicleAggregatedIdleEngines = ({ startDate, endDate, idleEngineThresholdInMinutes, enabled }: IdleEngineAggregationsRequest & { enabled: boolean }) =>
    useQuery<RawVehicleIdleEngine[]>({
      queryKey: [VEHICLE_AGGREGATED_KEY, startDate, endDate, idleEngineThresholdInMinutes],
      queryFn: () => getIdleEngineRequester().getVehicleIdleEngineAggregations(startDate, endDate, idleEngineThresholdInMinutes),
      staleTime: 300000,
      enabled,
    });

  const getDriverAggregatedIdleEngines = ({ startDate, endDate, idleEngineThresholdInMinutes, enabled }: IdleEngineAggregationsRequest & { enabled: boolean }) =>
    useQuery<RawDriverIdleEngine[]>({
      queryKey: [DRIVER_AGGREGATED_KEY, startDate, endDate, idleEngineThresholdInMinutes],
      queryFn: () => getIdleEngineRequester().getDriverIdleEngineAggregations(startDate, endDate, idleEngineThresholdInMinutes),
      staleTime: 300000,
      enabled,
    });

  const getVehicleIdleEngineEvents = ({ startDate, endDate, vehicleId, idleEngineThresholdInMinutes }: VehicleIdleEngineEventsRequest, enabled: boolean) =>
    useQuery<RawVehicleIdleEngineEvent[]>({
      queryKey: [VEHICLE_EVENTS_KEY, startDate, endDate, vehicleId, idleEngineThresholdInMinutes],
      queryFn: () => getIdleEngineRequester().getVehicleIdleEngineEvents(startDate, endDate, vehicleId, idleEngineThresholdInMinutes),
      staleTime: 300000,
      enabled,
    });

  const getDriverIdleEngineEvents = ({ startDate, endDate, driverId, idleEngineThresholdInMinutes }: DriverIdleEngineEventsRequest, enabled: boolean) =>
    useQuery<RawDriverIdleEngineEvent[]>({
      queryKey: [DRIVER_EVENTS_KEY, startDate, endDate, driverId, idleEngineThresholdInMinutes],
      queryFn: () => getIdleEngineRequester().getDriverIdleEngineEvents(startDate, endDate, driverId, idleEngineThresholdInMinutes),
      staleTime: 300000,
      enabled,
    });

  return {
    getVehicleAggregatedIdleEngines,
    getDriverAggregatedIdleEngines,
    getVehicleIdleEngineEvents,
    getDriverIdleEngineEvents,
  };
};

export default useIdleEngine;
